export const parseWeighbridgeWeight = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const WEIGHBRIDGE_KG_PER_MT = 1000;

export const normalizeWeighbridgeWeightToMt = (
  value,
  { digits = 2, sourceUnit = "auto" } = {},
) => {
  const parsedValue = parseWeighbridgeWeight(value);

  if (parsedValue === null) {
    return null;
  }

  let convertedValue = parsedValue;

  if (sourceUnit === "kg") {
    convertedValue = parsedValue / WEIGHBRIDGE_KG_PER_MT;
  } else if (sourceUnit === "auto") {
    // Weighbridge payloads now commonly arrive in MT, while some older integrations
    // still send kg-sized values. Truck ticket weights in kg are typically >= 1000.
    convertedValue =
      Math.abs(parsedValue) >= WEIGHBRIDGE_KG_PER_MT
        ? parsedValue / WEIGHBRIDGE_KG_PER_MT
        : parsedValue;
  }

  return Number(convertedValue.toFixed(digits));
};

export const convertWeighbridgeKgToMt = (value, digits = 2) =>
  normalizeWeighbridgeWeightToMt(value, { digits, sourceUnit: "kg" });

export const formatWeighbridgeWeightMt = (
  value,
  { digits = 2, fallback = "--", unit = "MT", sourceUnit = "auto" } = {},
) => {
  const convertedValue = normalizeWeighbridgeWeightToMt(value, {
    digits,
    sourceUnit,
  });

  if (convertedValue === null) {
    return fallback;
  }

  return `${convertedValue.toFixed(digits)} ${unit}`;
};

export const formatWeighbridgeWeightInputMt = (
  value,
  digits = 2,
  sourceUnit = "auto",
) => {
  const convertedValue = normalizeWeighbridgeWeightToMt(value, {
    digits,
    sourceUnit,
  });

  if (convertedValue === null) {
    return "";
  }

  return convertedValue.toFixed(digits);
};

export const computeWeighbridgeNetWeight = (firstWeight, secondWeight) => {
  const parsedFirstWeight = parseWeighbridgeWeight(firstWeight);
  const parsedSecondWeight = parseWeighbridgeWeight(secondWeight);

  if (parsedFirstWeight === null || parsedSecondWeight === null) {
    return null;
  }

  return parsedSecondWeight - parsedFirstWeight;
};

export const isWeighbridgeWeightSequenceValid = (
  firstWeight,
  secondWeight,
  netWeight = null,
) => {
  const parsedFirstWeight = parseWeighbridgeWeight(firstWeight);
  const parsedSecondWeight = parseWeighbridgeWeight(secondWeight);
  const parsedNetWeight = parseWeighbridgeWeight(netWeight);

  if (parsedFirstWeight === null || parsedSecondWeight === null) {
    return false;
  }

  if (parsedSecondWeight < parsedFirstWeight) {
    return false;
  }

  if (
    parsedNetWeight !== null &&
    (parsedNetWeight < 0 || parsedSecondWeight < parsedNetWeight)
  ) {
    return false;
  }

  return true;
};

export const computeGrnNetWeight = (firstWeight, secondWeight) => {
  const parsedFirstWeight = parseWeighbridgeWeight(firstWeight);
  const parsedSecondWeight = parseWeighbridgeWeight(secondWeight);

  if (parsedFirstWeight === null || parsedSecondWeight === null) {
    return null;
  }

  return parsedFirstWeight - parsedSecondWeight;
};

export const isGrnWeightSequenceValid = (
  firstWeight,
  secondWeight,
  netWeight = null,
) => {
  const parsedFirstWeight = parseWeighbridgeWeight(firstWeight);
  const parsedSecondWeight = parseWeighbridgeWeight(secondWeight);
  const parsedNetWeight = parseWeighbridgeWeight(netWeight);

  if (parsedFirstWeight === null || parsedSecondWeight === null) {
    return false;
  }

  if (parsedFirstWeight < parsedSecondWeight) {
    return false;
  }

  if (
    parsedNetWeight !== null &&
    (parsedNetWeight < 0 || parsedFirstWeight < parsedNetWeight)
  ) {
    return false;
  }

  return true;
};

export const resolveGrnNetWeight = (
  rawNetWeight,
  firstWeight,
  secondWeight,
  digits = 2,
) => {
  const parsedNetWeight = parseWeighbridgeWeight(rawNetWeight);
  const computedNetWeight = computeGrnNetWeight(firstWeight, secondWeight);
  const resolvedNetWeight =
    computedNetWeight !== null &&
    isGrnWeightSequenceValid(firstWeight, secondWeight, computedNetWeight)
      ? computedNetWeight
      : parsedNetWeight;

  if (resolvedNetWeight === null) {
    return "";
  }

  return resolvedNetWeight.toFixed(digits);
};

export const resolveWeighbridgeNetWeight = (
  rawNetWeight,
  firstWeight,
  secondWeight,
  digits = 2,
) => {
  const parsedNetWeight = parseWeighbridgeWeight(rawNetWeight);
  const computedNetWeight = computeWeighbridgeNetWeight(firstWeight, secondWeight);
  const resolvedNetWeight =
    computedNetWeight !== null &&
    isWeighbridgeWeightSequenceValid(firstWeight, secondWeight, computedNetWeight)
      ? computedNetWeight
      : parsedNetWeight;

  if (resolvedNetWeight === null) {
    return "";
  }

  return resolvedNetWeight.toFixed(digits);
};

export const mapWeighbridgeWeightsToGrn = (
  weights = {},
  digits = 2,
) => {
  const grossWeight = weights?.second_weight ?? "";
  const tareWeight = weights?.first_weight ?? "";

  return {
    first_weight: grossWeight,
    second_weight: tareWeight,
    net_weight: resolveGrnNetWeight(
      weights?.net_weight ?? "",
      grossWeight,
      tareWeight,
      digits,
    ),
  };
};
